import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Select, Form, notification, Typography, Input } from "antd";
import { LeftCircleOutlined } from "@ant-design/icons";
import {
  useLazyGetComplaintByIdQuery,
  useUpdateComplaintMutation
} from "../../../redux/services/complaintApi";
import { useLazyGetBrandModelsQuery } from "../../../redux/services/brandModelApi";
import ErrorDetails from "../../../components/ErrorDetails";
import { useNavigate, useParams } from "react-router-dom";
import {
  BrandModelSelect,
  BrandSelect,
  CheckBoxInput,
  CitySelect,
  CityManagerSelect,
  InputElement,
  ZoneSelect,
  PincodeInputElement,
  StateSelect,
  CustomerCitySelect
} from "../common/FormsElements";
import { useAppSelector } from "../../../redux/hooks";
import { yesNoOptions } from "../../../constants/helperConstants";

const ComplaintForm = (props: any) => {
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const [loading, setLoading] = useState(false);

  const params = useParams();

  const [update] = useUpdateComplaintMutation();

  const onUpdateComplaint = async (values: object, setLoading: any, form: any, id: any) => {
    try {
      setLoading(true);
      await update({ complaint: { id, ...values } }).unwrap();
      api.success({
        message: "Update complaint successful",
        placement: "top"
      });
      setLoading(false);
      form.resetFields();
      // setOpen(false);
      // refetch();
      if (window.history.length > 1) {
        history.back();
      } else {
        navigate("/complaints");
      }
    } catch (error: any) {
      api.info({
        message: "Update complaint failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onGoBack = () => {
    navigate(-1);
  };

  const [getComplaintById, { data }] = useLazyGetComplaintByIdQuery();
  const complaint = data?.complaint;

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const [brandId, setBrandId] = useState(complaint?.brand?.id);

  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis", "super_user"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );

  useEffect(() => {
    if (data) {
      const fieldsValue = { ...data.complaint };
      fieldsValue.assigned_city_manager_id = complaint?.assigned_city_manager?.id;
      fieldsValue.brand_id = complaint?.brand?.id;
      fieldsValue.brand_name = complaint?.brand?.name;
      fieldsValue.brand_model_id = complaint?.brand_model?.id;
      fieldsValue.brand_complaint_status = complaint?.status;
      fieldsValue.zone_id = complaint?.zone?.id;
      fieldsValue.city_id = complaint?.city?.id;
      fieldsValue.state_id = complaint?.state?.id;
      form.setFieldsValue(fieldsValue);
    }
  }, [data]);

  useEffect(() => {
    if (params?.complaintId) {
      getComplaintById(params.complaintId);
    }
  }, [params?.complaintId]);

  const onFinish = (values: any) => {
    onUpdateComplaint(values, setLoading, form, data.complaint.id);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const handleBrandSelect = (value: string) => {
    form.setFieldsValue({ brand_id: value });
    form.setFieldsValue({ brand_model_id: null });
    triggerGetBrandModels({ all_pages: true, brand_id: value });
    setBrandId(value);
  };

  useEffect(() => {
    const brandId = complaint?.brand?.id;
    if (brandId) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    }
  }, [complaint]);

  return (
    <div style={{ paddingBottom: 50 }}>
      <Row style={{ marginBottom: 20 }}>
        <Col
          sm={24}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <h2>Update Complaint</h2>
          <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
            Back
          </Button>
        </Col>
      </Row>

      {contextHolder}

      <Form
        name="complaint_form"
        form={form}
        initialValues={{ remember: true }}
        labelCol={{ span: 9 }}
        wrapperCol={{ span: 11 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Typography style={{ fontWeight: 500 }}>Customer Details</Typography>
        <Row gutter={16}>
          <Col span={12}>
            <InputElement
              label="Customer Name"
              name="customer_name"
              rules={[{ required: true, message: "Please Enter Customer Name" }]}
            />
            <InputElement label="Email" name="email" />
            <InputElement label="GST" name="gst" />
          </Col>
          <Col span={12}>
            <InputElement
              label="Contact Number"
              name="contact_number"
              rules={[
                { required: true, min: 10, message: "Contact Number must be minimum 10 digit." },
                { max: 10, message: "Contact Number be maximum 10 digit." },
                { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
              ]}
            />

            <InputElement
              label="Alt Contact Number"
              name="alt_contact_number"
              rules={[
                { min: 10, message: "Contact Number must be minimum 10 digit." },
                { max: 10, message: "Contact Number be maximum 10 digit." },
                { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
              ]}
            />
          </Col>
        </Row>

        <Typography style={{ fontWeight: 500 }}>Vehicle Details</Typography>
        <Row gutter={16}>
          <Col span={12}>
            <InputElement label="Dealer" name="dealer_name" />
            <BrandSelect onSelect={handleBrandSelect} />
            <InputElement label="Model Variant" name="model_variant" />
            <InputElement label="Model Colour" name="model_colour" />
          </Col>
          <Col span={12}>
            <InputElement label="Dealer Location" name="dealer_location" />
            <BrandModelSelect brandModels={brandModels} />
            <InputElement label="Model Description" name="model_description" />
            <InputElement label="Model Int Color" name="model_int_color" />
            <InputElement label="Model Ext Color" name="model_ext_color" />
          </Col>
        </Row>

        <Typography style={{ fontWeight: 500 }}>Address and Schedule</Typography>
        <Row gutter={16}>
          <Col span={12}>
            <PincodeInputElement />
            <InputElement label="Address Line2" name="address_line2" />
            <InputElement label="Latitude" name="latitude" />
            <ZoneSelect disabled={!isEditUser} />
            <Form.Item
              label="Is Outstation ?"
              name="is_outstation"
              rules={[{ required: true, message: "Please select outstation" }]}
            >
              <Select
                placeholder="Select Outstation"
                allowClear
                showSearch
                defaultValue
                optionFilterProp="children"
                filterOption={(inputValue, option: any) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={yesNoOptions}
              ></Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <InputElement label="Address Line1" name="address_line1" />
            <InputElement label="Locality" name="locality" />
            <InputElement label="Longitude" name="longitude" />
            <CustomerCitySelect />
            <CitySelect />
            <StateSelect required />
          </Col>
        </Row>

        <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
        <Row gutter={16}>
          <Col span={12}>
            <InputElement label="Company Name" name="company_name" />
            <CheckBoxInput label="Customer Ok With Recording" name="customer_ok_with_recording" />
            <CityManagerSelect />
            <InputElement label="Charger Type" name="obc" disabled={!(isSuperUser || isMisUser)} />
            <InputElement label="Model Name" name="complaint_brand_model_name" />
            <InputElement label="VIN Number" name="vin" />
          </Col>
          <Col span={12}>
            <InputElement label="Site POC Name" name="site_poc_name" />
            <InputElement label="Site POC Contact" name="site_poc_contact" />
            <InputElement label="Remarks" name="remarks" />
            {isSuperUser && (
              <Form.Item label="Complaint Recieve Date" name="complaint_receive_date">
                <Input type="date" />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Form.Item wrapperCol={{ offset: 19, span: 24 }}>
          <Row>
            <Col>
              <Space size={"middle"}>
                <Button
                  type="default"
                  onClick={() => {
                    form.resetFields();
                    onGoBack();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Update
                </Button>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ComplaintForm;
